import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { TrackAnswerEventRequest } from "../api/types";

type SummativeAssessmentStatus =
	| "idle"
	| "pending"
	| "assessing"
	| "assessed"
	| "error";

type SummativeRunState = {
	status: SummativeAssessmentStatus;
	dirty: boolean;
	seq: number;
	// Feedback-only: last serialized response a run was fired for, for hash-based dirty detection.
	lastRunResponse?: string;
};

const IDLE_RUN_STATE: SummativeRunState = {
	status: "idle",
	dirty: false,
	seq: 0,
};

type SummativeAssessmentState = {
	runs: Record<number, SummativeRunState>;
	feedbackStash: Record<number, TrackAnswerEventRequest>;
	strandedFeedbackItemIds: Record<number, true>;
	getRunState: (itemId: number) => SummativeRunState;
	shouldRunAssessment: (itemId: number) => boolean;
	shouldRunFeedback: (itemId: number, responseHash: string) => boolean;
	markResponseChanged: (itemId: number) => void;
	beginRun: (itemId: number, responseHash?: string) => number;
	isLatestRun: (itemId: number, seq: number) => boolean;
	markAssessed: (itemId: number, seq: number) => void;
	markError: (itemId: number, seq: number) => void;
	stashFeedbackRequest: (
		itemId: number,
		request: TrackAnswerEventRequest,
	) => void;
	getStashedFeedbackRequest: (
		itemId: number,
	) => TrackAnswerEventRequest | undefined;
	markFeedbackStranded: (itemId: number) => void;
	clearFeedbackStranded: (itemId: number) => void;
	isFeedbackStranded: (itemId: number) => boolean;
	reset: () => void;
};

const useSummativeAssessmentStore = create<SummativeAssessmentState>()(
	immer((set, get) => ({
		runs: {},
		feedbackStash: {},
		strandedFeedbackItemIds: {},
		getRunState: (itemId) => get().runs[itemId] ?? IDLE_RUN_STATE,
		shouldRunAssessment: (itemId) => {
			const run = get().runs[itemId];
			if (!run) {
				return false;
			}
			return run.status === "pending" || run.status === "error" || run.dirty;
		},
		// No container marks feedback dirty on every keystroke; a run is due whenever
		// the current response hash hasn't been graded yet, or the last grade errored.
		shouldRunFeedback: (itemId, responseHash) => {
			const run = get().runs[itemId];
			if (
				!run ||
				run.status === "idle" ||
				run.status === "pending" ||
				run.status === "error"
			) {
				return true;
			}
			return run.lastRunResponse !== responseHash;
		},
		markResponseChanged: (itemId) =>
			set((state) => {
				const run = state.runs[itemId];
				if (!run || run.status === "idle") {
					state.runs[itemId] = {
						status: "pending",
						dirty: false,
						seq: run?.seq ?? 0,
					};
					return;
				}
				if (run.status === "assessing" || run.status === "assessed") {
					run.dirty = true;
				}
			}),
		beginRun: (itemId, responseHash) => {
			let mySeq = 0;
			set((state) => {
				const run = state.runs[itemId] ?? IDLE_RUN_STATE;
				mySeq = run.seq + 1;
				state.runs[itemId] = {
					status: "assessing",
					dirty: false,
					seq: mySeq,
					lastRunResponse: responseHash ?? run.lastRunResponse,
				};
			});
			return mySeq;
		},
		isLatestRun: (itemId, seq) => (get().runs[itemId]?.seq ?? 0) === seq,
		markAssessed: (itemId, seq) =>
			set((state) => {
				const run = state.runs[itemId];
				if (!run || run.seq !== seq) {
					return;
				}
				run.status = "assessed";
			}),
		markError: (itemId, seq) =>
			set((state) => {
				const run = state.runs[itemId];
				if (!run || run.seq !== seq) {
					return;
				}
				run.status = "error";
			}),
		stashFeedbackRequest: (itemId, request) =>
			set((state) => {
				state.feedbackStash[itemId] = request;
			}),
		getStashedFeedbackRequest: (itemId) => get().feedbackStash[itemId],
		markFeedbackStranded: (itemId) =>
			set((state) => {
				state.strandedFeedbackItemIds[itemId] = true;
			}),
		clearFeedbackStranded: (itemId) =>
			set((state) => {
				delete state.strandedFeedbackItemIds[itemId];
			}),
		isFeedbackStranded: (itemId) =>
			Boolean(get().strandedFeedbackItemIds[itemId]),
		reset: () =>
			set({ runs: {}, feedbackStash: {}, strandedFeedbackItemIds: {} }),
	})),
);

export default useSummativeAssessmentStore;
